import { memo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LIST_BATCH, useComplianceList } from '../api/dashboards.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import {
  Chip, DashboardFrame, Waiting, listState, useUrlFilters,
} from '../components/DashboardFrame.tsx';
import { ListEnd } from '../components/ListEnd.tsx';
import { ScrollToTop } from '../components/ScrollToTop.tsx';
import { setRowLoader } from '../components/revealRegistry.ts';
import { useKeepPlace } from '../components/useKeepPlace.ts';
import { formatDate } from '../dates.ts';
import type { ReportRow, ReportState, ReportType } from '../api/types.ts';

/**
 * Every type the queue can hold, in the order a compliance director reads them:
 * the statutory closing obligations last, because they are the rarest and the
 * most serious. A type with nothing in the current view is not offered.
 */
const REPORT_TYPES: ReportType[] = ['Initial', 'Annual', 'Conversion Accounting', 'Statement of Death', 'Final'];


const URGENCIES: { value: '1' | '2' | '3'; label: string }[] = [
  { value: '1', label: 'Overdue' },
  { value: '2', label: 'Due within 30 days' },
  { value: '3', label: 'Later' },
];

const OUTSTANDING: ReportState[] = ['Not started', 'In preparation'];

/**
 * Neutral marks, all of them. Overdue is said in words beside the date, the
 * same way the tasks page says it; a filed report carries no colour at all,
 * because filing is the end of the obligation.
 */
const MARKS: Record<ReportState, string> = {
  'Not started': 'bg-white text-neutral-600 ring-1 ring-neutral-300',
  'In preparation': 'bg-note-bg text-note-fg ring-1 ring-note-edge',
  Filed: 'bg-white text-neutral-700 ring-1 ring-neutral-300',
};

type Approval = 'awaiting' | 'approved';

/**
 * The compliance dashboard: the reports owed to the court.
 *
 * **Outstanding** is the work: reports not started or in preparation, oldest
 * due first. **Filed** is the record: a filed report is done. Whether a court
 * also approved it is a detail, because approval orders read as local practice
 * from the document corpus — unconfirmed by counsel; see "Filing is the end;
 * approval is local practice" in docs/schema-decisions.md.
 *
 * "Awaiting approval" is a filter inside Filed and nothing more: no default,
 * no colour, no overdue count. In a court that does issue approvals a filed
 * report genuinely waits on one, and the filter lists the longest waits first
 * so they can be found. No county is named anywhere here.
 */
export function ComplianceDashboard() {
  return (
    <DashboardFrame
      dashboard="compliance"
      title="Reports"
      subtitle="Reports owed to the court. Outstanding is the work still to do; a filed report is done, and a court's approval, where one is issued, is recorded beside it."
    >
      <ComplianceBody />
    </DashboardFrame>
  );
}

function ComplianceBody() {
  const [params, update] = useUrlFilters();
  const view = params.get('view') === 'filed' ? 'filed' : 'outstanding';
  const state = view === 'outstanding' ? params.get('state') ?? undefined : undefined;
  const urgency = view === 'outstanding' ? params.get('urgency') ?? undefined : undefined;
  const approvalParam = params.get('approval');
  const approval: Approval | undefined =
    view === 'filed' && (approvalParam === 'awaiting' || approvalParam === 'approved') ? approvalParam : undefined;
  // Type and county compose with everything else and with each other.
  const type = params.get('type') ?? undefined;
  const county = params.get('county') ?? undefined;
  // Deceased and discharged clients are shown unless asked otherwise — the
  // opposite of Visits, because their reporting obligations are live.
  const closed = params.get('closed') === 'hide' ? 'hide' : undefined;
  // Outstanding: soonest due first. Awaiting approval: longest with the court
  // first. Everything else filed: most recently filed first.
  const sort = view === 'outstanding' ? 'due_date' : approval === 'awaiting' ? 'filed_date' : '-filed_date';

  // Every filter and the sort are in the key, so changing one is a different
  // list: it starts again at the top, in the new order, from the server. The
  // rows already loaded are never reordered in the page.
  const filters = { view, state, urgency, approval, type, county, closed, sort } as const;
  const list = useComplianceList(filters);
  const { failure, takeover } = listState({
    error: list.error,
    failureReason: list.failureReason,
    isPlaceholderData: list.isPlaceholderData,
    data: list.head,
  });
  const data = takeover ? undefined : list.head;

  // `lost` is rendered below: see the note in DirectorDashboard.
  const place = useKeepPlace(`reports:${JSON.stringify(filters)}`, list, LIST_BATCH);

  // The quick search loads up to a report further down this list rather than
  // taking the reader to the client's record and losing their place.
  useEffect(() => {
    setRowLoader(async (clientId) => {
      const { found } = await list.loadUntil((rows) => rows.some((r) => r.client_id === clientId));
      return found;
    });
    return () => setRowLoader(null);
  }, [list]);
  const states = data?.state_counts ?? {};
  const urgencies = data?.urgency_counts ?? {};
  const approvals = data?.approval_counts;
  const typeCounts = data?.type_counts ?? {};
  const countyCounts = data?.county_counts ?? {};
  const counties = data?.counties ?? [];
  const outstanding = (states['Not started'] ?? 0) + (states['In preparation'] ?? 0);
  const rows = takeover ? [] : list.rows;

  return (
    <>
      <div className="space-y-2 border-b border-neutral-200 px-4 py-3 lg:px-5">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Which reports">
          <Chip active={view === 'outstanding'} onClick={() => update({ view: null, approval: null, offset: null })}>
            Outstanding <span className="font-normal">{outstanding.toLocaleString()}</span>
          </Chip>
          <Chip
            active={view === 'filed'}
            onClick={() => update({ view: 'filed', state: null, urgency: null, offset: null })}
          >
            Filed <span className="font-normal">{(states.Filed ?? 0).toLocaleString()}</span>
          </Chip>
        </div>

        {view === 'outstanding' ? (
          <>
            <div className="flex flex-wrap gap-2" role="group" aria-label="When due">
              <Chip active={!urgency} onClick={() => update({ urgency: null, offset: null })}>
                Any date
              </Chip>
              {URGENCIES.map((u) => (
                <Chip
                  key={u.value}
                  active={urgency === u.value}
                  onClick={() => update({ urgency: u.value, offset: null })}
                >
                  {u.label} <span className="font-normal">{(urgencies[u.value] ?? 0).toLocaleString()}</span>
                </Chip>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="State">
              <Chip active={!state} onClick={() => update({ state: null, offset: null })}>
                Any state
              </Chip>
              {OUTSTANDING.map((s) => (
                <Chip key={s} active={state === s} onClick={() => update({ state: s, offset: null })}>
                  {s} <span className="font-normal">{(states[s] ?? 0).toLocaleString()}</span>
                </Chip>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Approval">
              <Chip active={!approval} onClick={() => update({ approval: null, offset: null })}>
                All filed
              </Chip>
              <Chip active={approval === 'awaiting'} onClick={() => update({ approval: 'awaiting', offset: null })}>
                Awaiting approval <span className="font-normal">{(approvals?.awaiting ?? 0).toLocaleString()}</span>
              </Chip>
              <Chip active={approval === 'approved'} onClick={() => update({ approval: 'approved', offset: null })}>
                Approved <span className="font-normal">{(approvals?.approved ?? 0).toLocaleString()}</span>
              </Chip>
            </div>
            {approval === 'awaiting' && (
              <p className="max-w-[60rem] text-sm text-neutral-600">
                Filed reports with no approval order recorded, longest with the court first. Many courts
                never issue one, so this is not a list of problems: it is where to look when a court that
                does approve reports has taken a long time.
              </p>
            )}
          </>
        )}

        {/*
          Type and county compose: with each other, and with everything above.
          Each chip's count is what it would return in the current view, beside
          whatever else is chosen.
        */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Report type">
          <Chip active={!type} onClick={() => update({ type: null, offset: null })}>
            Every type
          </Chip>
          {REPORT_TYPES.filter((t) => (typeCounts[t] ?? 0) > 0 || type === t).map((t) => (
            <Chip key={t} active={type === t} onClick={() => update({ type: t, offset: null })}>
              {t} <span className="font-normal">{(typeCounts[t] ?? 0).toLocaleString()}</span>
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="reports-county" className="sr-only">County</label>
          <select
            id="reports-county"
            value={county ?? ''}
            onChange={(e) => update({ county: e.target.value, offset: null })}
            className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2 text-[0.9375rem]
                       text-neutral-800"
          >
            <option value="">Every county</option>
            {counties
              .filter((c) => (countyCounts[c.code] ?? 0) > 0 || county === c.code)
              .map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label} · {(countyCounts[c.code] ?? 0).toLocaleString()}
                </option>
              ))}
          </select>
          {(countyCounts.none ?? 0) > 0 && (
            <p className="text-sm text-neutral-500">
              {(countyCounts.none ?? 0).toLocaleString()} with no county recorded
            </p>
          )}

          {/*
            Shown by default, and this hides them. The opposite of the visits
            dashboard on purpose: the statement of death and the final account
            are due because the client died, so a closed case has live work.
          */}
          <Chip
            active={closed === 'hide'}
            onClick={() => update({ closed: closed === 'hide' ? null : 'hide', offset: null })}
          >
            Hide deceased and discharged{' '}
            <span className="font-normal">{(data?.closed_clients ?? 0).toLocaleString()}</span>
          </Chip>
        </div>
      </div>

      {!takeover && (
        <Staleness
          updatedAt={list.dataUpdatedAt}
          isFetching={list.loading && !list.appending}
          error={list.error}
          onRefresh={() => list.refetch()}
        />
      )}

      {failure && takeover ? (
        <LoadFailure
          what="Reports"
          error={failure}
          onRetry={() => list.refetch()}
          retrying={list.loading}
          forbiddenIsSession={false}
        />
      ) : list.pending ? (
        <Waiting>Gathering the reports…</Waiting>
      ) : !takeover && rows.length === 0 ? (
        <Waiting>{view === 'filed' ? 'No filed reports match these filters.' : 'No outstanding reports match these filters.'}</Waiting>
      ) : (
        <>
          {/* see the note in DirectorDashboard: the restore is the only thing
              that loads rows without the reader scrolling, and it never lands
              silently */}
          {place.restoring && (
            <p role="status" className="border-b border-neutral-200 bg-neutral-50 px-5 py-2 text-sm text-neutral-600">
              Getting you back to where you were…
            </p>
          )}
          {place.lost && (
            <p role="status" className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-sm text-amber-900">
              {place.lost}
            </p>
          )}
          {/* sticky: the columns stay readable however far down the list runs,
              which they did not need to be while it was paged */}
          <div className={`sticky top-0 z-10 hidden border-b border-neutral-200 bg-white px-5 py-2 text-xs
                           font-bold tracking-[0.12em] text-neutral-500 uppercase ${COLUMNS} lg:!grid`}>
            <span>Client</span>
            <span>Report</span>
            <span>{view === 'filed' ? 'Filed' : 'Due'}</span>
            <span className="text-right">State</span>
          </div>
          <ol className="divide-y divide-neutral-200">
            {rows.map((row) => <Row key={row.deadline_id} row={row} />)}
          </ol>
          {/* asks for the next batch a screenful early, and holds its own height
              so appending never moves the rows above it */}
          <ListEnd
            loadMore={list.loadMore}
            hasMore={list.hasMore}
            appending={list.appending}
            loaded={rows.length}
            total={list.total}
            noun="reports"
          />
        </>
      )}

      <ScrollToTop />

    </>
  );
}

const COLUMNS =
  'lg:grid lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)_17rem_10rem] lg:items-center lg:gap-4';

function plural(n: number, word: string): string {
  return `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
}

/** When, in words. Only outstanding work can be overdue. */
function when(row: ReportRow): { primary: string; secondary: string; overdue: boolean } {
  if (row.state === 'Filed') {
    const filed = row.filed_date ? `Filed ${formatDate(row.filed_date)}` : 'Filed';
    const secondary = row.approved_date
      ? `Approved ${formatDate(row.approved_date)}`
      : row.days_since_filed !== null && row.days_since_filed !== undefined
        ? `No approval recorded · ${plural(row.days_since_filed, 'day')} since filing`
        : 'No approval recorded';
    return { primary: filed, secondary, overdue: false };
  }
  const due = `${row.is_provisional ? 'about ' : ''}${formatDate(row.due_date)}`;
  if (row.days_until < 0) return { primary: due, secondary: `${plural(-row.days_until, 'day')} overdue`, overdue: true };
  if (row.days_until === 0) return { primary: due, secondary: 'Due today', overdue: false };
  return { primary: due, secondary: `in ${plural(row.days_until, 'day')}`, overdue: false };
}

/**
 * Memoised, and that is load-bearing rather than tidy.
 *
 * Appending a batch re-renders `ComplianceBody`, and without this every row
 * already on screen re-rendered with it — so the cost of adding fifty rows was
 * the cost of reconciling all of them. Measured on the filed view before this:
 * 112ms at 400 rows mounted, 406–417ms at 2,000, 504–589ms at 2,600, rising
 * almost exactly linearly at about 0.19ms a mounted row.
 *
 * `row` is safe to compare by identity: react-query keeps each page object, so
 * the row objects are the same references across an append — only the flattened
 * array is rebuilt. The `state` object and the `when()` result are created
 * inside, so they cost nothing unless this row actually re-renders.
 */
const Row = memo(function Row({ row }: { row: ReportRow }) {
  const w = when(row);

  return (
    <li data-quick-client={row.client_id}>
      <Link
        to={`/clients/${row.client_id}`}
        // so the client's back control returns to this list, and the place in it
        state={{ from: 'Reports' }}
        className={`block min-h-[64px] px-4 py-3 hover:bg-neutral-50 active:bg-neutral-100 lg:min-h-[56px]
                    lg:px-5 lg:py-2.5 ${COLUMNS}`}
      >
        <div className="min-w-0">
          <div className="flex min-w-0 items-baseline gap-2">
            <p className="min-w-0 truncate font-semibold text-neutral-900">{row.display_name}</p>
            <span className="shrink-0 font-mono text-sm text-neutral-500">{row.client_ref}</span>
          </div>
          {/* the index number the court knows the case by: the latest order's */}
          <p className="truncate font-mono text-xs text-neutral-500">
            {row.court_index_number ?? 'No index number'}
            {row.county && <span className="font-sans"> · {row.county}</span>}
          </p>
        </div>
        <div className="mt-0.5 min-w-0 lg:mt-0">
          <p className="truncate text-[0.9375rem] text-neutral-800">{row.label}</p>
          <p className="text-sm text-neutral-500">
            {row.state === 'Filed' ? `${row.report_type} · due ${formatDate(row.due_date)}` : row.report_type}
            {row.is_statutory && ' · Statutory'}
          </p>
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-2 lg:mt-0 lg:block">
          <p className="text-sm text-neutral-700 tabular-nums lg:text-[0.9375rem]">
            {w.primary}
            {row.state !== 'Filed' && row.is_provisional && (
              <span className="ml-1 text-xs text-neutral-500">estimate</span>
            )}
          </p>
          <p className={`text-sm tabular-nums ${w.overdue ? 'font-semibold text-critical-edge' : 'text-neutral-500'}`}>
            {w.secondary}
          </p>
        </div>
        <div className="mt-1 lg:mt-0 lg:text-right">
          <span className={`inline-flex rounded px-2 py-0.5 text-xs font-bold tracking-wide uppercase ${MARKS[row.state]}`}>
            {row.state}
          </span>
        </div>
      </Link>
    </li>
  );
});
